/**
 * Grand Quack Auto — combo multiplier system.
 *
 * Tony Hawk / Goat Simulator style: chain chaos events within a time window
 * for escalating multipliers and ridiculous combo names.
 *
 * Per research §8: "the single highest-leverage feature for Twitter virality
 * after the core quack mechanic."
 */

import type { System, World } from '../types';
import { getHtmlVolume } from '../utils/audioMixer';
import { triggerDollyZoom, triggerFovPunch, triggerShake, triggerSlowMo } from './juiceSystem';
import { triggerConfettiBurst } from './particleBurstSystem';
import { triggerDeathScreen } from '../ui/deathScreen';
import { showPopup, triggerComboBannerSweep } from '../ui/scorePopups';
import { pulseShareButton } from '../ui/shareButton';

// ── Combo names by threshold ──────────────────────────────────────────────

const COMBO_NAMES: [number, string][] = [
  [10, 'ABSOLUTE QUACKERY'],
  [8, 'DUCK DYNASTY'],
  [6, 'QUACK ATTACK'],
  [4, 'FOWL PLAY'],
  [2, 'DOUBLE TROUBLE'],
];

function getComboName(count: number): string {
  for (const [threshold, name] of COMBO_NAMES) {
    if (count >= threshold) return name;
  }
  return '';
}

// ── Module state ──────────────────────────────────────────────────────────

let comboCount = 0;
let comboTimer = 0;
let maxComboReached = 0; // session high-water mark — used by statsScreen.
let lastTier = 0;        // highest tier (2/4/6/8/10) reached this combo run
const COMBO_WINDOW = 3.0;
let comboEl: HTMLElement | null = null;
let quackeryTriggered = false; // one-time flag for ABSOLUTE QUACKERY

const COMBO_TIERS = [2, 4, 6, 8, 10] as const;
function tierOf(count: number): number {
  let t = 0;
  for (const v of COMBO_TIERS) if (count >= v) t = v;
  return t;
}

// ── Exports ───────────────────────────────────────────────────────────────

/** Called by chaosSystem when addChaos fires. Returns the current multiplier. */
export function registerComboHit(world: World): number {
  comboCount++;
  comboTimer = COMBO_WINDOW;
  if (comboCount > maxComboReached) maxComboReached = comboCount;

  if (comboCount >= 4) {
    const name = getComboName(comboCount);
    showPopup(world, `combo_${name}_x${comboCount}`);
  }
  if (comboCount >= 6) triggerFovPunch();

  // Tier crossing — fire confetti every time, dolly-zoom at x6+, banner at x8/x10.
  const tier = tierOf(comboCount);
  if (tier > lastTier) {
    triggerConfettiBurst(world, world.duck.position, { count: 30 });
    if (tier >= 6) triggerDollyZoom();
    if (tier >= 8) triggerComboBannerSweep(`${getComboName(tier)} x${tier}`);
    lastTier = tier;
  }

  // ABSOLUTE QUACKERY — the ultimate rage moment at x10+.
  if (comboCount >= 10 && !quackeryTriggered) {
    quackeryTriggered = true;
    showAbsoluteQuackery(world);
  }

  return Math.max(1, comboCount);
}

export function getComboCount(): number { return comboCount; }
export function getComboName2(): string { return getComboName(comboCount); }
/** Session-high combo count + matching tier name. Used by statsScreen. */
export function getMaxCombo(): number { return maxComboReached; }
export function getMaxComboName(): string { return getComboName(maxComboReached); }

/** The ultimate rage moment — ABSOLUTE QUACKERY at x10 combo. */
function showAbsoluteQuackery(world: World): void {
  // Don't show if death/stats already up.
  if (document.getElementById('death-screen') || document.getElementById('stats-screen')) return;

  // Phase 1: FREEZE + heavy effects.
  world.time.timeScale = 0;
  triggerShake(1.5);

  // Phase 2: Full-screen rage overlay.
  const overlay = document.createElement('div');
  overlay.id = 'absolute-quackery';
  overlay.innerHTML = `
    <div class="aq-title">ABSOLUTE</div>
    <div class="aq-title aq-title2">QUACKERY</div>
    <div class="aq-combo">x${comboCount} COMBO</div>
    <div class="aq-duck">🔫🦆🔥</div>
  `;
  document.body.appendChild(overlay);

  // Pulse the floating share button so the player notices it's there for
  // the inevitable "wait, I have to share this" moment. 5s window covers
  // the cinematic + the death-screen transition.
  pulseShareButton(5000);

  // Phase 3: Sound — layered quacks + explosion.
  setTimeout(() => {
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        const q = new Audio('/sounds/quack.mp3');
        q.playbackRate = 0.8 + i * 0.15;
        q.volume = getHtmlVolume(0.7);
        q.play().catch(() => {});
      }, i * 100);
    }
  }, 300);

  // Phase 4: After 4s, remove overlay + trigger death screen.
  setTimeout(() => {
    world.time.timeScale = 1;
    overlay.remove();
    triggerDeathScreen(world);
  }, 4000);
}

// ── System ────────────────────────────────────────────────────────────────

export const comboSystem: System = {
  name: 'combo',

  fixedUpdate(dt: number, world: World) {
    if (comboCount === 0) return;

    comboTimer -= dt;
    if (comboTimer <= 0) {
      // Combo ended — show final result if significant.
      if (comboCount >= 3) {
        const name = getComboName(comboCount);
        showPopup(world, `end_${name}_x${comboCount}`);
      }
      comboCount = 0;
      comboTimer = 0;
      lastTier = 0; // reset so the next combo run re-fires the celebrations
    }
  },

  render(_dt: number, _alpha: number, _world: World) {
    // Update combo HUD element.
    if (!comboEl) {
      comboEl = document.getElementById('hud-combo');
      if (!comboEl) {
        const hud = document.getElementById('hud');
        if (hud) {
          comboEl = document.createElement('div');
          comboEl.id = 'hud-combo';
          comboEl.style.cssText =
            'position:absolute;top:80px;left:50%;transform:translateX(-50%);' +
            'text-align:center;color:#ff4444;font-family:Bangers,cursive;' +
            'font-size:24px;letter-spacing:3px;' +
            'text-shadow:2px 2px 0 #000,0 0 10px rgba(255,0,0,0.5);';
          hud.appendChild(comboEl);
        }
      }
    }

    if (comboEl) {
      if (comboCount >= 2) {
        const name = getComboName(comboCount);
        comboEl.textContent = `${name} x${comboCount}`;
        comboEl.style.display = 'block';
        // Color escalates: orange → red → purple.
        const intensity = Math.min(comboCount / 10, 1);
        const r = Math.floor(255);
        const g = Math.floor(100 * (1 - intensity));
        const b = Math.floor(255 * intensity);
        comboEl.style.color = `rgb(${r},${g},${b})`;
        comboEl.style.fontSize = `${14 + comboCount}px`;
      } else {
        comboEl.style.display = 'none';
      }
    }
  },
};
